import React from 'react';
import { Check, ArrowRight, Users, MapPin, Video, Clock, BookOpen, Sparkles } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function Workshops() {
  const {
    t
  } = useLanguage();
  const inPersonWorkshops = [{
    id: 'team-dynamics',
    icon: <Users size={32} data-id="element-942" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    borderColor: 'border-coral/20',
    tKey: 'teamDynamics'
  }, {
    id: 'leadership-intensive',
    icon: <Sparkles size={32} data-id="element-943" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    borderColor: 'border-teal/20',
    tKey: 'leadershipIntensive'
  }];
  const onlineWorkshops = [{
    id: 'virtual-facilitation',
    icon: <Video size={32} data-id="element-944" />,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    tKey: 'virtualFacilitation'
  }, {
    id: 'remote-team',
    icon: <BookOpen size={32} data-id="element-945" />,
    color: 'bg-yellow-500',
    lightColor: 'bg-yellow-100',
    textColor: 'text-yellow-600',
    borderColor: 'border-yellow-200',
    tKey: 'remoteTeam'
  }];
  const renderWorkshopCard = (workshop: (typeof inPersonWorkshops)[0], index: number, isOnline: boolean) => {
    const benefits = t(`workshops.items.${workshop.tKey}.benefits`) as string[];
    return <Animate key={workshop.id} delay={index * 0.1} data-id="element-946">
        <Card className={`p-0 overflow-hidden border-2 ${workshop.borderColor}`} data-id="element-947">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-948">
            <div className={`lg:col-span-4 ${workshop.lightColor} p-10 flex flex-col justify-center items-center text-center relative overflow-hidden`} data-id="element-949">
              <div className={`w-20 h-20 rounded-full ${workshop.color} text-white flex items-center justify-center mb-6 shadow-lg relative z-10`} data-id="element-950">
                {workshop.icon}
              </div>
              <h3 className={`text-2xl font-bold ${workshop.textColor} mb-2 relative z-10`} data-id="element-951">
                {t(`workshops.items.${workshop.tKey}.title`)}
              </h3>
              <p className="font-medium text-gray-600 relative z-10" data-id="element-952">
                {t(`workshops.items.${workshop.tKey}.subtitle`)}
              </p>
            </div>
            <div className="lg:col-span-8 p-8 md:p-12 flex flex-col justify-center bg-white" data-id="element-953">
              <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-954">
                {t(`workshops.items.${workshop.tKey}.description`)}
              </p>
              <div className="flex flex-wrap gap-4 mb-6 text-sm" data-id="element-955">
                <div className="flex items-center gap-2 text-gray-500" data-id="element-956">
                  <Clock size={16} className={workshop.textColor} data-id="element-957" />
                  <span data-id="element-958">{t(`workshops.items.${workshop.tKey}.duration`)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500" data-id="element-959">
                  {isOnline ? <Video size={16} className={workshop.textColor} data-id="element-960" /> : <MapPin size={16} className={workshop.textColor} data-id="element-961" />}
                  <span data-id="element-962">{t(`workshops.items.${workshop.tKey}.format`)}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8" data-id="element-963">
                {benefits.map((benefit: string, i: number) => <div key={i} className="flex items-start" data-id="element-964">
                    <div className={`mt-1 mr-3 p-1 rounded-full ${workshop.lightColor} ${workshop.textColor}`} data-id="element-965">
                      <Check size={14} strokeWidth={3} data-id="element-966" />
                    </div>
                    <span className="text-gray-700 font-medium" data-id="element-967">{benefit}</span>
                  </div>)}
              </div>
              <div className="pt-6 border-t border-gray-100" data-id="element-968">
                <Link to="/contact" data-id="element-969">
                  <Button variant={isOnline ? 'secondary' : 'primary'} rightIcon={<ArrowRight size={18} data-id="element-971" />} data-id="element-970">
                    {t('workshops.getQuote')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Card>
      </Animate>;
  };
  return <PageTransition data-id="element-972">
      <div className="pt-12 pb-24" data-id="element-973">
        <section className="text-center max-w-4xl mx-auto px-4 mb-20 relative" data-id="element-974">
          <DecorativeShape type="circle" color="bg-coral/10" className="top-0 left-0 w-40 h-40 -z-10" data-id="element-975" />
          <DecorativeShape type="square" color="bg-teal/10" className="bottom-0 right-0 w-32 h-32 rotate-12 -z-10" data-id="element-976" />
          <Animate data-id="element-977">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-978">
              {t('workshops.badge')}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-979">
              {t('workshops.title')} <br data-id="element-980" />
              <span className="text-coral" data-id="element-981">
                {t('workshops.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-982">
              {t('workshops.subtitle')}
            </p>
          </Animate>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-983">
          <Animate className="flex items-center gap-4 mb-12" data-id="element-984">
            <div className="p-3 bg-coral/10 rounded-2xl" data-id="element-985">
              <MapPin size={28} className="text-coral" data-id="element-986" />
            </div>
            <div data-id="element-987">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-988">
                {t('workshops.inPersonTitle')}
              </h2>
              <p className="text-gray-500" data-id="element-989">{t('workshops.inPersonSubtitle')}</p>
            </div>
          </Animate>
          <div className="space-y-8" data-id="element-990">
            {inPersonWorkshops.map((w, i) => renderWorkshopCard(w, i, false))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-991">
          <Animate className="flex items-center gap-4 mb-12" data-id="element-992">
            <div className="p-3 bg-blue-50 rounded-2xl" data-id="element-993">
              <Video size={28} className="text-blue-600" data-id="element-994" />
            </div>
            <div data-id="element-995">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-996">
                {t('workshops.onlineTitle')}
              </h2>
              <p className="text-gray-500" data-id="element-997">{t('workshops.onlineSubtitle')}</p>
            </div>
          </Animate>
          <div className="space-y-8" data-id="element-998">
            {onlineWorkshops.map((w, i) => renderWorkshopCard(w, i, true))}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 mt-24 text-center" data-id="element-999">
          <Animate data-id="element-1000">
            <Card className="p-12 bg-gradient-to-br from-coral/5 to-teal/5 border-0" data-id="element-1001">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1002">
                {t('workshops.customTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1003">
                {t('workshops.customDescription')}
              </p>
              <Link to="/contact" data-id="element-1004">
                <Button variant="primary" rightIcon={<ArrowRight size={18} data-id="element-1006" />} data-id="element-1005">
                  {t('workshops.customCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}